import { FormControl, FormGroup } from '@angular/forms';

import { MissionAcceptanceType } from '@enums/mission-acceptance-type.enum';
import { DateHelper } from '@helpers/date.helper';
import { FormHelper } from '@helpers/form.helper';
import { ErrorMessage } from '@interfaces/error-message';
import { MissionAcceptanceInformations } from '@models/mission-acceptance/mission-acceptance-informations';
import { ToFormGroupType } from '@modules/core/types/to-form-group';

export type MissionAcceptanceInformationsFormType = ToFormGroupType<Required<MissionAcceptanceInformations>>;

const MISSING_IDENTITY_PIECE_ERROR = 'missing-identity-piece';

export class MissionAcceptanceInformationsForm extends FormGroup<MissionAcceptanceInformationsFormType> {
  get typeErrorMessage(): ErrorMessage | null {
    return FormHelper.getEmptyErrorMessage(this.controls.type, 'Le type de dossier est requis');
  }

  get missingIdentityPieceErrorMessage(): ErrorMessage | null {
    if (this.hasError(MISSING_IDENTITY_PIECE_ERROR)) {
      return {
        error: 'Champ requis',
        message: `La pièce d'identité du dirigeant est requise`
      };
    }

    return null;
  }

  constructor() {
    super({
      id: new FormControl<number | undefined>(undefined),
      type: new FormControl<MissionAcceptanceType | null>(null),
      monthlyBilledPlan: new FormControl<number | null>(null),
      caForecastAmount: new FormControl<number | null>(null),
      activityComment: new FormControl<string | null>(null),
      previousAccountantContacted: new FormControl<boolean | null>(null),
      previousAccountantLetterDate: new FormControl<string | null>(null),
      previousAccountantOppositionExisted: new FormControl<boolean | null>(null),
      directorsOwnersStrangerCompanies: new FormControl<boolean | null>(null),
      directorsFrenchResidents: new FormControl<boolean | null>(null),
      directorsOwnersLiquidationCompanies: new FormControl<boolean | null>(null),
      directorsOwnersOtherAccountantCompanies: new FormControl<boolean | null>(null)
    });
  }

  fromMissionAcceptanceInformations(missionAcceptanceInformations: MissionAcceptanceInformations): void {
    this.patchValue({
      id: missionAcceptanceInformations.id,
      type: missionAcceptanceInformations.type,
      monthlyBilledPlan: missionAcceptanceInformations.monthlyBilledPlan,
      caForecastAmount: missionAcceptanceInformations.caForecastAmount,
      activityComment: missionAcceptanceInformations.activityComment,
      previousAccountantContacted: missionAcceptanceInformations.previousAccountantContacted,
      previousAccountantLetterDate: missionAcceptanceInformations.previousAccountantLetterDate,
      previousAccountantOppositionExisted: missionAcceptanceInformations.previousAccountantOppositionExisted,
      directorsOwnersStrangerCompanies: missionAcceptanceInformations.directorsOwnersStrangerCompanies,
      directorsFrenchResidents: missionAcceptanceInformations.directorsFrenchResidents,
      directorsOwnersLiquidationCompanies: missionAcceptanceInformations.directorsOwnersLiquidationCompanies,
      directorsOwnersOtherAccountantCompanies: missionAcceptanceInformations.directorsOwnersOtherAccountantCompanies
    });
  }

  toMissionAcceptanceInformations(): MissionAcceptanceInformations {
    const missionAcceptanceInformations = new MissionAcceptanceInformations();

    missionAcceptanceInformations.id = this.controls.id.value;
    missionAcceptanceInformations.type = this.controls.type.value ?? null;
    missionAcceptanceInformations.monthlyBilledPlan = this.controls.monthlyBilledPlan.value ?? null;
    missionAcceptanceInformations.caForecastAmount = this.controls.caForecastAmount.value ?? null;
    missionAcceptanceInformations.activityComment = this.controls.activityComment.value ?? null;
    missionAcceptanceInformations.previousAccountantContacted = this.controls.previousAccountantContacted.value ?? null;
    missionAcceptanceInformations.previousAccountantLetterDate = this.controls.previousAccountantLetterDate.value
      ? DateHelper.format(this.controls.previousAccountantLetterDate.value)
      : null;
    missionAcceptanceInformations.previousAccountantOppositionExisted =
      this.controls.previousAccountantOppositionExisted.value ?? null;
    missionAcceptanceInformations.directorsOwnersStrangerCompanies =
      this.controls.directorsOwnersStrangerCompanies.value ?? null;
    missionAcceptanceInformations.directorsFrenchResidents = this.controls.directorsFrenchResidents.value ?? null;
    missionAcceptanceInformations.directorsOwnersLiquidationCompanies =
      this.controls.directorsOwnersLiquidationCompanies.value ?? null;
    missionAcceptanceInformations.directorsOwnersOtherAccountantCompanies =
      this.controls.directorsOwnersOtherAccountantCompanies.value ?? null;

    return missionAcceptanceInformations;
  }

  clearNotDisplayedFieldsByType(): void {
    if (this.controls.type.value !== MissionAcceptanceType.previousAccountantRecovery) {
      this.patchValue({
        previousAccountantContacted: null,
        previousAccountantLetterDate: null,
        previousAccountantOppositionExisted: null
      });
    }

    if (this.controls.type.value !== MissionAcceptanceType.creation) {
      this.patchValue({
        directorsOwnersStrangerCompanies: null,
        directorsFrenchResidents: null,
        directorsOwnersLiquidationCompanies: null,
        directorsOwnersOtherAccountantCompanies: null
      });
    }
  }

  addMissingIdentityPieceError(): void {
    FormHelper.addErrorToControl(this, MISSING_IDENTITY_PIECE_ERROR);
  }

  removeMissingIdentityPieceError(): void {
    FormHelper.removeErrorFromControl(this, MISSING_IDENTITY_PIECE_ERROR);
  }
}
